import {
    Pipe, PipeTransform
} from "@angular/core";

import {
    DateTime
} from "luxon";
import {
    DateTimeDisplayFormat, DateTimeMode
} from "@shopliftr/common-js/shared";

/**
 * Angular pipe for formatting dates using Luxon DateTime with timezone support.
 *
 * Converts various date input formats (Date objects, ISO strings, timestamps)
 * to formatted date strings using customizable formats and timezones.
 * Uses the common-js DateTimeDisplayFormat for consistent formatting across the application.
 *
 * @example
 * ```html
 * <!-- Basic date formatting -->
 * {{ myDate | mgdCommonDate }}
 *
 * <!-- Custom format -->
 * {{ myDate | mgdCommonDate:'yyyy-MM-dd' }}
 *
 * <!-- Custom format with timezone -->
 * {{ myDate | mgdCommonDate:'MMM dd, yyyy':'America/New_York' }}
 *
 * <!-- ISO string input -->
 * {{ '2023-12-25T10:30:00Z' | mgdCommonDate }}
 *
 * <!-- Timestamp input -->
 * {{ 1703505000000 | mgdCommonDate:'dd/MM/yyyy' }}
 * ```
 */
@Pipe({ name: "mgdCommonDate" })
export class CommonDatePipe implements PipeTransform {

    /**
     * Transforms a date value into a formatted string.
     *
     * @param value - The date to format. Accepts:
     * - Date object: JavaScript Date instance
     * - string: ISO 8601 date string (e.g., '2023-12-25T10:30:00Z')
     * - number: Unix timestamp in milliseconds
     * @param format - Optional format string using Luxon format tokens.
     * Defaults to DateTimeDisplayFormat.getFormat(DateTimeMode.Date)
     * Common formats:
     * - 'yyyy-MM-dd' → '2023-12-25'
     * - 'MMM dd, yyyy' → 'Dec 25, 2023'
     * - 'dd/MM/yyyy HH:mm' → '25/12/2023 10:30'
     * @param timezone - Optional timezone identifier (e.g., 'America/New_York', 'UTC').
     * Defaults to the local timezone
     * @returns Formatted date string, or empty string if input is null/undefined
     *
     * @example
     * ```typescript
     * const pipe = new CommonDatePipe();
     *
     * // Format current date
     * pipe.transform(new Date()); // '12/25/2023' (using default format)
     *
     * // Custom format
     * pipe.transform(new Date(), 'yyyy-MM-dd'); // '2023-12-25'
     *
     * // With timezone
     * pipe.transform(new Date(), 'MMM dd, yyyy HH:mm', 'UTC'); // 'Dec 25, 2023 15:30'
     * ```
     */
    transform(value: Date | number | string, format?: string, timezone?: string): string {

        if (!value) {
            return "";
        }

        format = format || DateTimeDisplayFormat.getFormat(DateTimeMode.Date);

        let dateTime: DateTime;

        if (value instanceof Date) {
            dateTime = DateTime.fromJSDate(value);
        }
        else if (typeof (value) === "string") {
            dateTime = DateTime.fromISO(value);
        }
        else {
            dateTime = DateTime.fromMillis(value);
        }

        return dateTime.setZone(timezone || DateTime.local().zone).toFormat(format);
    }
}
